import { Button, HStack } from "@chakra-ui/react";
import { Table } from "@/ui/tables/Table";
import { getStoredPageSize } from "@/ui/tables/pageSize";
import type { WorkOrder, WorkOrderInput } from "../type/WorkOrder";
import { CreateWorkOrderModal } from "./CreateWorkOrderModal";
import {
  useGetWorkOrder,
  type WorkOrderFilters,
  type WorkOrderStatus,
} from "../useCases/useGetWorkOrder";
import { useGetClientEntities } from "@/core/clientEntities/useCases/useGetClientEntities";
import { FilterChips, type FilterChipOption } from "@/ui/tables/FilterChips";
import { withTotal } from "@/ui/tables/filterChipCounts";
import { TableFilters } from "@/ui/filters/TableFilters";
import {
  countActiveFilters,
  getDateRange,
  getOption,
  toDateRangeParams,
  type FilterField,
  type FilterValues,
} from "@/ui/filters/filterValues";
import { UpdateWorkOrderModal } from "./UpdateWorkOrderModal";
import { CloseWorkOrderModal } from "./CloseWorkOrderModal";
import { useCreateWorkOrder } from "../useCases/useCreateWorkOrder";
import { useUpdateWorkOrder } from "../useCases/useUpdateWorkOrder";
import { useCallback, useEffect, useMemo, useState } from "react";
import { notify } from "@/ui/chakra/notify";
import { useConfirmToggle } from "@/ui/modals/useConfirm";
import { LuLock, LuPencil, LuPlus } from "react-icons/lu";
import { Pagination } from "@/shared/core-configs";
import { useDebounce } from "@/core/common/hooks/useDebounce";
import { WorkOrdersTableColumns } from "../Columns";
import { useAuth } from "@/shared/session";
import type { Action } from "@/ui/tables/Table";
import { PageContainer } from "@/ui/containers/PageContainer";

type StatusChip = "all" | WorkOrderStatus;

const STATUS_CHIPS: FilterChipOption<StatusChip>[] = [
  { value: "all", label: "Todas" },
  { value: "open", label: "Abiertas" },
  { value: "closed", label: "Cerradas" },
];

export const WorkOrderTable = () => {
  const { session } = useAuth();
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    pageSize: getStoredPageSize(),
  });
  const [showDisabled, setShowDisabled] = useState(false);

  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(
    null,
  );
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isCloseModalOpen, setCloseModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState<string>();

  const [order, setOrder] = useState<{
    sort?: string;
    order?: string;
  }>({
    sort: undefined,
    order: undefined,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { data: clientEntities } = useGetClientEntities({
    zoneIds: session?.zone ?? "",
  });
  const filterFields = useMemo<FilterField[]>(
    () => [
      { type: "dateRange", key: "date", label: "Fecha de creación" },
      {
        type: "select",
        key: "clientEntity",
        label: "Empresa mandante",
        tagLabel: "Empresa",
        placeholder: "Todas",
        options: clientEntities.map((c) => ({ value: c.id, label: c.name })),
      },
    ],
    [clientEntities],
  );
  const [statusFilter, setStatusFilter] = useState<StatusChip>("all");
  const [extraFilters, setExtraFilters] = useState<FilterValues>({});
  const filters = useMemo<WorkOrderFilters>(
    () => ({
      ...toDateRangeParams(getDateRange(extraFilters, "date")),
      clientEntityId: getOption(extraFilters, "clientEntity")?.value,
      status: statusFilter === "all" ? undefined : statusFilter,
    }),
    [extraFilters, statusFilter],
  );
  const hasActiveFilters =
    statusFilter !== "all" || countActiveFilters(extraFilters) > 0;

  const {
    data: workOrders = [],
    refetch,
    isPending,
    pagination: metaWorkOrder,
  } = useGetWorkOrder({
    page: pagination.page,
    pageSize: pagination.pageSize,
    showDisabled,
    searchTerm: debouncedSearchTerm,
    ...order,
    zoneIds: session?.zone ?? undefined,
    filters,
  });

  const { mutate: createWorkOrder } = useCreateWorkOrder();
  const { mutate: updateWorkOrder } = useUpdateWorkOrder();

  const handleOpenCreateModal = () => setCreateModalOpen(true);
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  const handleOpenEditModal = (workOrderId: string) => {
    setSelectedWorkOrderId(workOrderId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedWorkOrderId(null);
  };

  const [selectedWorkOrderForClose, setSelectedWorkOrderForClose] =
    useState<WorkOrder | null>(null);

  const handleOpenCloseModal = (workOrder: WorkOrder) => {
    setSelectedWorkOrderForClose(workOrder);
    setSelectedWorkOrderId(workOrder.id);
    setCloseModalOpen(true);
  };

  const handleCloseCloseModal = () => {
    setCloseModalOpen(false);
    setSelectedWorkOrderId(null);
    setSelectedWorkOrderForClose(null);
  };

  const handleCreateWorkOrder = (data: WorkOrderInput) => {
    createWorkOrder(data, {
      onSuccess: () => {
        setCreateModalOpen(false);
        refetch();
      },
    });
  };

  const confirmToggle = useConfirmToggle();

  const handleToggleEnabled = useCallback(
    async (workOrder: WorkOrder) => {
      const ok = await confirmToggle(
        workOrder.enabled,
        `la orden de trabajo ${workOrder.code}`,
      );
      if (!ok) return;
      const updatedWorkOrder: WorkOrderInput = {
        name: workOrder.name,
        code: workOrder.code,
        zoneId: workOrder.zone.id,
        enabled: !workOrder.enabled,
      };

      updateWorkOrder(
        { id: workOrder.id, updatedWorkOrder },
        {
          onSuccess: () => {
            notify.success(
              workOrder.enabled
                ? "Orden de trabajo deshabilitada"
                : "Orden de trabajo habilitada",
            );
            refetch();
          },
          onError: (error) =>
            notify.error("No se pudo actualizar la orden de trabajo", error),
        },
      );
    },
    [updateWorkOrder, refetch, confirmToggle],
  );

  const workOrdersTableColumns = useMemo(
    () => WorkOrdersTableColumns(handleToggleEnabled),
    [handleToggleEnabled],
  );

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm, filters, showDisabled]);

  return (
    <PageContainer
      display="flex"
      flexDir="column"
      gap={4}
      title="Órdenes de trabajo"
      description="Crea y administra las órdenes de trabajo según las necesidades."
      actions={
        <Button colorPalette="brand" onClick={handleOpenCreateModal}>
          <LuPlus />
          Crear orden de trabajo
        </Button>
      }
    >
      <Table
        searchPlaceholder="Buscar por código o nombre…"
        pageSize={pagination.pageSize}
        onChangePageSize={(pageSize) => setPagination({ page: 1, pageSize })}
        page={pagination.page}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={() => {
          setStatusFilter("all");
          setExtraFilters({});
        }}
        filters={
          <HStack gap={1.5} flexWrap="wrap">
            <FilterChips
              options={STATUS_CHIPS}
              value={statusFilter}
              onChange={setStatusFilter}
              counts={withTotal(metaWorkOrder?.countByStatus, "open", "closed")}
            />
            <TableFilters
              fields={filterFields}
              value={extraFilters}
              onChange={setExtraFilters}
            />
          </HStack>
        }
        emptyDescription="Las órdenes de trabajo agrupan los vales de un mismo trabajo."
        emptyAction={
          <Button
            colorPalette="brand"
            size="sm"
            onClick={handleOpenCreateModal}
          >
            Crear orden de trabajo
          </Button>
        }
        headers={workOrdersTableColumns}
        data={workOrders}
        extractItemKey={(item: WorkOrder) => item.id}
        actions={
          [
            {
              label: "Editar",
              icon: <LuPencil size={18} />,
              onClick: (item) => handleOpenEditModal((item as WorkOrder).id),
            },
            {
              label: (item) =>
                (item as WorkOrder).closedWorkOrder
                  ? "Actualizar cierre de OT"
                  : "Cerrar OT",
              icon: <LuLock size={18} />,
              onClick: (item) => handleOpenCloseModal(item as WorkOrder),
            },
          ] as Action[]
        }
        pages={metaWorkOrder?.totalPages}
        totalItems={metaWorkOrder?.count}
        itemsLabel="órdenes"
        showDisabledCheck
        onSortChange={(sort, order) => {
          setOrder({ sort, order });
        }}
        onShowDisabledChange={(showDisabled) => setShowDisabled(showDisabled)}
        onSearch={(searchTerm) => {
          setSearchTerm(searchTerm);
        }}
        onChangePage={(page) => {
          setPagination({ ...pagination, page: page + 1 });
        }}
        hiddenColumnsKey="workOrders"
        isLoading={isPending}
      />

      <CreateWorkOrderModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateWorkOrder}
      />

      {selectedWorkOrderId && (
        <UpdateWorkOrderModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          workOrderId={selectedWorkOrderId}
        />
      )}

      {selectedWorkOrderId && (
        <CloseWorkOrderModal
          isOpen={isCloseModalOpen}
          onClose={handleCloseCloseModal}
          workOrderId={selectedWorkOrderId}
          closedWorkOrder={selectedWorkOrderForClose?.closedWorkOrder}
          onSuccess={() => refetch()}
        />
      )}
    </PageContainer>
  );
};
